import React, { useContext, useEffect, useState } from "react";
import {
  Table,
  Space,
  Button,
  Input,
  Drawer,
  TablePaginationConfig,
  Tooltip,
} from "antd";
import { PageHeader } from "../../../components/page-header/page-header";
import useFetch from "../../../hooks/useFetch";
import {
  GetScannedProductList,
  UpdateCrateMapping,
} from "../../../utils/api.constant";
import {
  ePrivileges,
  eResultCode,
  eWARRANTY_STATUS,
  eOUTPUT_STATUS,
  eEXCEL_STATUS,
  eREGIONS,
  eDATE_FORMAT,
} from "../../../utils/enum";
import useToast from "../../../hooks/useToast";
import { ToastType, ToastOpen } from "../../../state/toast/slice";
import CustomDrawer from "../../../components/drawer";
import { TScanResult } from "../../../types/index";
import Utils from "../../../utils";
import { UserContext } from "../../../context";
import { TContext, TPrivilege, TResponseModel } from "../../../types/config";
import {
  FilterOutlined,
  IdcardFilled,
  OrderedListOutlined,
  SearchOutlined,
  CloseCircleFilled,
  CheckCircleFilled,
} from "@ant-design/icons";
import ColumnManager from "../../../components/configurable-columns";
import ExportToCSV from "../../../components/exporttocsv/ExportToCsv";
import DateUtils from "../../../utils/date";
import {
  saveColumnPreferences,
  loadColumnPreferences,
  clearColumnPreferences,
} from "../../../utils/column-management";
import CrateAssignForm from "./crate-assign-form";
import { useColumnSearch } from "../../../components/table/column-search";

const LEVEL1_LIST_COLUMN_KEY = "level1List";

type TProps = {
  onCloseDrawer: () => void;
};

export default function ScannedBlowerList(props: TProps) {
  const { post } = useFetch();
  const { onShowToast } = useToast();
  const [isLoading, setIsLoading] = useState(true);
  const [data, setData] = useState<TScanResult[]>([]);
  const context: TContext = useContext(UserContext);
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [globalsearchText, setGlobalSearchText] = useState("");
  const { getColumnSearchProps } = useColumnSearch<TScanResult>();

  const initialColumns: any[] = [
    {
      title: "Airtech Model",
      dataIndex: "airtechModel",
      key: "airtechModel",
      ...getColumnSearchProps("airtechModel"),
      filterSearch: true,
      sorter: (a: TScanResult, b: TScanResult) =>
        a.serialNumber.localeCompare(b.airtechModel),
      width: 190,
    },
    {
      title: "Part Number",
      dataIndex: "partNumber",
      key: "partNumber",
      ...getColumnSearchProps("partNumber"),
      filterSearch: true,
      sorter: (a: TScanResult, b: TScanResult) =>
        a.serialNumber.localeCompare(b.partNumber),
      width: 150,
    },
    {
      title: "Unit Serial Number",
      dataIndex: "serialNumber",
      key: "serialNumber",
      ...getColumnSearchProps("serialNumber"),
      filterSearch: true,
      sorter: (a: TScanResult, b: TScanResult) =>
        a.serialNumber.localeCompare(b.serialNumber),
      width: 220,
    },
    {
      title: "Crate Serial Number",
      dataIndex: "crateSerialNo",
      key: "crateSerialNo",
      sorter: (a: TScanResult, b: TScanResult) =>
        (a.crateSerialNo || "").localeCompare(b.crateSerialNo || ""),
      ...getColumnSearchProps("crateSerialNo"),
      filterSearch: true,
      width: 250,
    },
    // {
    //   title: "Excel Status",
    //   dataIndex: "excelStatus",
    //   key: "excelStatus",
    //   sorter: (a: TScanResult, b: TScanResult) => a.excelStatus - b.excelStatus,
    //   filters: [
    //     { text: "Present", value: eEXCEL_STATUS.PRESENT },
    //     { text: "Not Present", value: eEXCEL_STATUS.NOT_PRESENT },
    //   ],
    //   onFilter: (value: string | number | boolean, record: TScanResult) =>
    //     record.excelStatus === value,
    //   render: (status: eEXCEL_STATUS) =>
    //     status === eEXCEL_STATUS.PRESENT ? "Present" : "Not Present",
    // },
    {
      title: "Debrief Date",
      dataIndex: "debriefDateString",
      key: "debriefDateString",
      sorter: (a: TScanResult, b: TScanResult) =>
        new Date(a?.debriefDateString || "")?.getTime() -
        new Date(b?.debriefDateString || "")?.getTime(),
      width: 150,
    },
    {
      title: "Manufacturing Date",
      dataIndex: "manufacturingDateString",
      key: "manufacturingDateString",
      sorter: (a: TScanResult, b: TScanResult) =>
        new Date(a?.manufacturingDateString || "").getTime() -
        new Date(b?.manufacturingDateString || "").getTime(),
      width: 180,
    },
    {
      title: "Warranty Date",
      dataIndex: "warrantyDateString",
      key: "warrantyDateString",
      sorter: (a: TScanResult, b: TScanResult) =>
        new Date(a?.warrantyDateString || "").getTime() -
        new Date(b?.warrantyDateString || "").getTime(),
      width: 150,
    },
    // {
    //   title: "Excel Upload Date",
    //   dataIndex: "excelUploadDate",
    //   key: "excelUploadDate",
    //   sorter: (a: TScanResult, b: TScanResult) =>
    //     new Date(a.excelUploadDate).getTime() -
    //     new Date(b.excelUploadDate).getTime(),
    // },

    // {
    //   title: "Warranty Message",
    //   dataIndex: "warrantyMessage",
    //   key: "warrantyMessage",
    // },
    {
      title: "Excel Message",
      dataIndex: "excelMessage",
      key: "excelMessage",
      width: 400,
      ...getColumnSearchProps("excelMessage"),
      filterSearch: true,
    },
    {
      title: "Warranty Status",
      dataIndex: "warrantyStatus",
      key: "warrantyStatus",
      width: 280,
      sorter: (a: TScanResult, b: TScanResult) =>
        a.warrantyStatus - b.warrantyStatus,
      filters: [
        { text: "In Warranty", value: eWARRANTY_STATUS.IN_WARRANTY },
        { text: "Out of Warranty", value: eWARRANTY_STATUS.OUT_OF_WARRANTY },
        {
          text: "In Warranty & Claimed",
          value: eWARRANTY_STATUS.IN_WARRANTY_CLAIMED,
        },
        {
          text: "In Warranty & Not Claimed",
          value: eWARRANTY_STATUS.IN_WARRANTY_NOT_CLAIMED,
        },
      ],
      onFilter: (value: string | number | boolean, record: TScanResult) =>
        record.warrantyStatus === value,
      render: (status: eWARRANTY_STATUS, record: TScanResult) => (
        <span
          style={{
            backgroundColor:
              record.outputStatus === eOUTPUT_STATUS.GREEN
                ? "var(--bs-success)"
                : record.outputStatus === eOUTPUT_STATUS.RED
                  ? "var(--bs-danger)"
                  : "",
            color: record.outputStatus ? "white" : "#333",
            padding: "4px 8px",
            borderRadius: "4px",
          }}
        >
          {status === eWARRANTY_STATUS.IN_WARRANTY
            ? "In Warranty"
            : status === eWARRANTY_STATUS.IN_WARRANTY_CLAIMED
              ? "In Warranty and Claimed"
              : status === eWARRANTY_STATUS.IN_WARRANTY_NOT_CLAIMED
                ? "In Warranty and Not Claimed"
                : status === eWARRANTY_STATUS.OUT_OF_WARRANTY
                  ? "Out of Warranty"
                  : "-"}
        </span>
      ),
    },
    {
      title: "Scanned/Received Date",
      dataIndex: "scannedDate",
      key: "scannedDate",
      sorter: (a: TScanResult, b: TScanResult) =>
        new Date(a.scannedDate!).getTime() - new Date(b.scannedDate!).getTime(),
      render: (scannedDate: string) => {
        return DateUtils.format(scannedDate, context?.user?.dateFormat, true);
      },
      width: 250,
    },
  ];
  const [columns, setColumns] = useState(initialColumns);
  const [draftColumns, setDraftColumns] = useState(columns);
  const [drawerVisible, setDrawerVisible] = useState(false);

  useEffect(() => {
    (async () => {
      const savedPreferences = await loadColumnPreferences(
        LEVEL1_LIST_COLUMN_KEY,
      );
      if (savedPreferences) {
        const initialColumnsMap = new Map(
          initialColumns.map((col) => [col.key, col]),
        );
        const reorderedColumns = savedPreferences
          .map((pref: any) => {
            const col = initialColumnsMap.get(pref.key);
            if (col) {
              return { ...col, hidden: pref.hidden };
            }
            return null;
          })
          .filter((col: any) => col !== null);

        setColumns(reorderedColumns as any[]);
        setDraftColumns(reorderedColumns as any[]);
      }
    })();
  }, []);

  const fetchData = async () => {
    try {
      setIsLoading(true);
      const requestPayload = {
        data: {
          currentPage: 1,
          pageSize: -1,
        },
      };

      const response: TResponseModel = await post(
        GetScannedProductList,
        requestPayload,
      );
      const { dataResponse, data } = response;
      if (dataResponse.returnCode === eResultCode.SUCCESS) {
        setData(data);
        // updateColumnFilters(data);
      } else {
        onShowToast({
          type: ToastType.error,
          title: <CloseCircleFilled />,
          position: ToastOpen.leftBottom,
          content: dataResponse.description,
        });
      }
    } catch (error) {
      console.error("Error:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // const updateColumnFilters = (dataset: TScanResult[]) => {
  //   const serialNumberFilters = utils.getUniqueFilters(dataset, "serialNumber");
  //   const crateSerialNoFilters = utils.getUniqueFilters(
  //     dataset,
  //     "crateSerialNo"
  //   );

  //   setColumns((prevColumns) =>
  //     prevColumns.map((col) => {
  //       if (col.key === "serialNumber") {
  //         return { ...col, filters: serialNumberFilters };
  //       }
  //       if (col.key === "crateSerialNo") {
  //         return { ...col, filters: crateSerialNoFilters };
  //       }
  //       return col;
  //     })
  //   );
  // };

  const filteredData = data.filter((item) => {
    if (!globalsearchText) return true;
    return Object.values(item).some((val) =>
      String(val).toLowerCase().includes(globalsearchText.toLowerCase()),
    );
  });

  const handleTableChange = (
    pagination: TablePaginationConfig,
    filters: Record<string, any>,
    sorter: any,
  ) => {
    setColumns((prevColumns) =>
      prevColumns.map((col) => {
        if ("dataIndex" in col) {
          return {
            ...col,
            filteredValue: filters[col.dataIndex as string] || null,
            sortOrder: col.dataIndex === sorter.field ? sorter.order : null,
          };
        }
        return col;
      }),
    );
  };

  const handleDragEnd = (result: any) => {
    if (!result.destination) return;

    setDraftColumns((prevDraftColumns) => {
      const reorderedColumns = Array.from(prevDraftColumns);
      const [removed] = reorderedColumns.splice(result.source.index, 1);
      reorderedColumns.splice(result.destination.index, 0, removed);
      return reorderedColumns;
    });
  };

  const toggleColumn = (key: string) => {
    setDraftColumns((prevDraftColumns) =>
      prevDraftColumns.map((col) =>
        col.key === key ? { ...col, hidden: !col.hidden } : col,
      ),
    );
  };

  const handleResetColumns = () => {
    setDraftColumns(initialColumns); // Reset staging state to initial state
    clearColumnPreferences(LEVEL1_LIST_COLUMN_KEY);
  };

  const handleSaveColumns = () => {
    setColumns(draftColumns); // Apply changes to the final state
    saveColumnPreferences(LEVEL1_LIST_COLUMN_KEY, draftColumns);
    setDrawerVisible(false); // Close the drawer
  };

  const visibleColumns = columns?.filter((col) => !col?.hidden);

  const mapRecordForExport = (rec: any) => {
    const copy: any = { ...rec };

    const formatExportDate = (dateVal: any, showTime: boolean = false) => {
      return DateUtils.formatForExport(
        dateVal,
        showTime,
        context?.user?.dateFormat as eDATE_FORMAT,
      );
    };

    copy.debriefDateString = formatExportDate(copy.debriefDate);
    copy.manufacturingDateString = formatExportDate(copy.manufacturingDate);
    copy.warrantyDateString = formatExportDate(copy.warrantyDate);
    copy.scannedDate = formatExportDate(copy.scannedDate, true);

    if (copy.warrantyStatus !== undefined && copy.warrantyStatus !== null) {
      const st = Number(copy.warrantyStatus);
      copy.warrantyStatus =
        st === eWARRANTY_STATUS.IN_WARRANTY
          ? "In Warranty"
          : st === eWARRANTY_STATUS.IN_WARRANTY_CLAIMED
            ? "In Warranty and Claimed"
            : st === eWARRANTY_STATUS.IN_WARRANTY_NOT_CLAIMED
              ? "In Warranty and Not Claimed"
              : st === eWARRANTY_STATUS.OUT_OF_WARRANTY
                ? "Out of Warranty"
                : copy.warrantyStatus;
    }

    if (copy.outputStatus !== undefined && copy.outputStatus !== null) {
      const s = Number(copy.outputStatus);
      copy.outputStatus =
        s === eOUTPUT_STATUS.GREEN
          ? "Green"
          : s === eOUTPUT_STATUS.RED
            ? "Red"
            : copy.outputStatus;
    }

    if (copy.excelStatus !== undefined && copy.excelStatus !== null) {
      const s = Number(copy.excelStatus);
      copy.excelStatus =
        s === eEXCEL_STATUS.PRESENT
          ? "Present"
          : s === eEXCEL_STATUS.NOT_PRESENT
            ? "Not Present"
            : copy.excelStatus;
    }

    if (copy.isActive !== undefined && copy.isActive !== null) {
      copy.isActive = copy.isActive ? "Active" : "Inactive";
    }

    return copy;
  };

  const applyColumnFilters = (rows: TScanResult[], cols: any[]) => {
    let result = rows;
    cols.forEach((col) => {
      const dataIndex = col?.dataIndex;
      const filteredValues = col?.filteredValue;
      if (!dataIndex || !filteredValues || filteredValues.length === 0) return;

      result = result.filter((r) => {
        const cellValue = r[dataIndex as keyof TScanResult];
        return filteredValues.some((fv: any) => {
          if (cellValue === undefined || cellValue === null) return false;
          // Match exact or string-contains for convenience (numbers vs labels)
          try {
            return (
              String(cellValue).toLowerCase() === String(fv).toLowerCase() ||
              String(cellValue).toLowerCase().includes(String(fv).toLowerCase())
            );
          } catch (e) {
            return false;
          }
        });
      });
    });
    return result;
  };

  const exportData = data.map((item) => ({ ...mapRecordForExport(item) }));

  const rowsAfterGlobalSearch = filteredData;
  const rowsAfterColumnFilters = applyColumnFilters(
    rowsAfterGlobalSearch,
    columns || [],
  );
  const exportFilteredData = rowsAfterColumnFilters.map((item) => ({
    ...mapRecordForExport(item),
  }));

  const handleResetAll = () => {
    setColumns((prevColumns) =>
      prevColumns.map((col) => ({
        ...col,
        filteredValue: null,
        sortOrder: null,
      })),
    );
  };

  const onSelectChange = (newSelectedRowKeys: React.Key[]) => {
    setSelectedRowKeys(newSelectedRowKeys);
  };

  const rowSelection = {
    selectedRowKeys,
    onChange: onSelectChange,
  };

  const unAssignedCrates = async () => {
    try {
      const payload = {
        data: {
          id: 0, // value is 0 because no crate is required
          emptyCrate: true, // to unassigned the creates
          scannedProductList: selectedRowKeys.map((id) => ({ id })),
        },
      };
      setIsLoading(true);

      const response = await post(UpdateCrateMapping, payload);
      if (response.dataResponse.returnCode === eResultCode.SUCCESS) {
        onShowToast({
          type: ToastType.success,
          title: <CheckCircleFilled />,
          position: ToastOpen.leftBottom,
          content: response.dataResponse.description,
        });
        fetchData();
        setSelectedRowKeys([]);
      } else {
        onShowToast({
          type: ToastType.error,
          title: <CloseCircleFilled />,
          position: ToastOpen.leftBottom,
          content: response.dataResponse.description,
        });
      }
    } catch (error) {
      console.error("Error:", error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="pageBody">
      <PageHeader
        icon={<IdcardFilled />}
        title={`${data?.length} Total Blower Scanned | ${selectedRowKeys.length} Selected`}
        size="large"
      >
        <Space>
          <Button
            type="default"
            loading={isLoading}
            disabled={isLoading}
            onClick={props.onCloseDrawer}
          >
            Close
          </Button>
          {Utils.isUserHasAccess(
            context.user?.privilegeList as TPrivilege[],
            ePrivileges.ASSIGNCRATE,
          ) ? (
            <CustomDrawer
              trigger={
                <Button
                  disabled={isLoading || !selectedRowKeys?.length}
                  type="primary"
                >
                  Assign Crate
                </Button>
              }
              component={CrateAssignForm}
              componentProps={{
                scannedIds: selectedRowKeys,
                onRefreshList: () => {
                  fetchData();
                  setSelectedRowKeys([]);
                },
              }}
              title="Crate Assignment"
              size="default"
            />
          ) : null}

          {Utils.isUserHasAccess(
            context.user?.privilegeList as TPrivilege[],
            ePrivileges.ASSIGNCRATE,
          ) ? (
            <Button
              disabled={isLoading || !selectedRowKeys?.length}
              type="default"
              onClick={async () => await unAssignedCrates()}
            >
              Unassign Crate
            </Button>
          ) : null}
          <Input
            placeholder="Filter by keywords"
            value={globalsearchText}
            onChange={(e) => setGlobalSearchText(e.target.value)}
            style={{ width: 200, borderColor: "var(--bs-primary-light)" }}
            prefix={<SearchOutlined />}
          />
          <ExportToCSV
            data={exportData}
            filteredData={exportFilteredData}
            visibleColumns={visibleColumns}
            moduleName="Blower List"
            allColumns={initialColumns}
          />
          <Tooltip title="Reset Filters">
            <FilterOutlined onClick={handleResetAll} />
          </Tooltip>
          <Tooltip title="Configure Columns">
            <OrderedListOutlined
              onClick={() => {
                setDraftColumns(columns);
                setDrawerVisible(true);
              }}
            />
          </Tooltip>
        </Space>
      </PageHeader>

      <div className="tableWrap">
        <Table
          rowSelection={rowSelection}
          columns={visibleColumns}
          dataSource={filteredData}
          loading={isLoading}
          pagination={{
            position: ["bottomLeft"],
            showSizeChanger: true,
            pageSizeOptions: ["10", "20", "50", "100"],
            showTotal: (total, range) =>
              `Showing ${range[0]}-${range[1]} of ${total} total entries`,
          }}
          onChange={handleTableChange}
          bordered={false}
          size="small"
          sticky
          scroll={{
            y: "calc(100vh - 310px)", // Adjust height dynamically
            x: "max-content",
          }}
          rowKey="id"
        />
      </div>

      <Drawer
        title="Customize Columns"
        placement="right"
        onClose={() => setDrawerVisible(false)}
        open={drawerVisible}
        style={{ transition: "transform 0.3s ease-in-out" }}
      >
        <ColumnManager
          columns={draftColumns}
          initialColumns={initialColumns}
          onDragEnd={handleDragEnd}
          onToggleColumn={toggleColumn}
          onResetColumns={handleResetColumns}
          onSave={handleSaveColumns}
          onCancel={() => setDrawerVisible(false)}
        />
      </Drawer>
    </div>
  );
}
